/**
 * ASK-POPUP-01 — Ask Pegasus, everywhere (Seth 2026-08-19: "ask pegasus
 * questions and it will use all of its knowledge and records … easily
 * accessible on all pages … a pop up so you don't have to leave your page").
 *
 * A floating button in the shell opens a small chat panel; questions go to the
 * `pegasus-assistant` edge function, which answers FROM PEGASUS RECORDS via
 * read-only lookups (customers, board, jobs, agreements, permits, invoices).
 * It can't write anything — it finds, explains, and points at the right page.
 * The current route rides along as context, so on a 360 "when were we here
 * last?" just works. (Distinct from the Reports-page analyst, which computes
 * governed numbers — this one is for mid-call "look it up for me" moments.)
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, CornerDownLeft, Loader2, ThumbsUp, ThumbsDown } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { setAiOpen, useDockState } from "@/shell/dockState";

interface Msg { role: "user" | "model"; text: string; logId?: number | null; verdict?: "right" | "wrong" | null }

const SUGGESTIONS = [
  "Who do we refer for duct cleaning?",
  "Who's on the board today?",
  "What have we charged Verde for filters?",
  "Average tech time on a call?",
  "When did we last order a CK40CT coil?",
  "Which agreements expire this month?",
];

/** Tiny renderer: paragraphs + **bold** — nothing heavier in a popup. */
function Bold({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold text-slate-800">{p}</strong> : <span key={i}>{p}</span>))}
    </>
  );
}

/** Bold + CLICKABLE record links — [PO 12345](/purchase-orders?escpo=12345)
 *  navigates in-app (Seth 2026-08-21: "pull up the purchase order in
 *  question"). Web links open a new tab. Anything else in link form (the
 *  model once wrote a folder path as a link) shows as plain bold text —
 *  never raw markdown (ASK-PEGASUS-02). */
function AnswerText({ text }: { text: string }) {
  const navigate = useNavigate();
  const segs = text.split(/(\[[^\]]+\]\([^)]*\))/g);
  return (
    <span className="whitespace-pre-wrap">
      {segs.map((seg, i) => {
        const m = seg.match(/^\[([^\]]+)\]\(([^)]*)\)$/);
        if (!m) return <Bold key={i} text={seg} />;
        const label = m[1];
        const href = m[2].trim();
        if (!href.startsWith("/") && !/^https?:\/\//i.test(href)) {
          return <strong key={i} className="font-semibold text-slate-800">{label}</strong>;
        }
        if (href.startsWith("/")) {
          return (
            <button key={i} type="button" onClick={() => navigate(href)}
              className="font-semibold text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-900">
              {label}
            </button>
          );
        }
        return <a key={i} href={href} target="_blank" rel="noreferrer" className="font-semibold text-brand-700 underline">{label}</a>;
      })}
    </span>
  );
}

export function AskPegasusPopup() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  // DOCK-01: tell the corner we're open (chat tucks away) + tuck OUR button
  // away while the chat panel is up.
  const { chatOpen } = useDockState();
  useEffect(() => { setAiOpen(open); return () => setAiOpen(false); }, [open]);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // C360-BOARD-01: the 360's "Ask about {name}…" strip opens this popup —
  // context (path + custno) already rides along on every question.
  // 360-POLISH-03 (Seth 2026-10-13): the strip is a real input now — a typed
  // question arrives in detail.q and is ASKED on open, not just focused.
  const [pendingQ, setPendingQ] = useState<string | null>(null);
  useEffect(() => {
    const onOpen = (e: Event) => {
      setOpen(true);
      const q = (e as CustomEvent<{ q?: string }>).detail?.q;
      if (q && q.trim()) setPendingQ(q.trim());
    };
    window.addEventListener("pegasus:ask-open", onOpen);
    return () => window.removeEventListener("pegasus:ask-open", onOpen);
  }, []);

  // ASK-REVIEW-01: right / wrong lands on this question's log row; a wrong
  // one shows up in Settings → Ask Pegasus → Needs a Look for an admin to teach.
  async function rate(i: number, verdict: "right" | "wrong") {
    const m = msgs[i];
    if (!m?.logId) return;
    const next = m.verdict === verdict ? null : verdict;
    setMsgs((all) => all.map((x, j) => (j === i ? { ...x, verdict: next } : x)));
    await supabase.rpc("assistant_log_feedback", { p_id: m.logId, p_verdict: next });
  }

  async function ask(text: string) {
    const q = text.trim();
    if (!q || busy) return;
    setInput("");
    const next: Msg[] = [...msgs, { role: "user", text: q }];
    setMsgs(next);
    setBusy(true);
    try {
      // Context: the page they're on (and the customer, when they're on a 360).
      const custno = location.pathname.match(/\/customers\/([^/]+)/)?.[1] ?? null;
      const { data, error } = await supabase.functions.invoke("pegasus-assistant", {
        body: { messages: next.map(({ role, text }) => ({ role, text })), context: { path: location.pathname, custno } },
      });
      if (error) throw error;
      setMsgs((m) => [...m, { role: "model", text: String(data?.answer ?? "No answer came back — try again."), logId: typeof data?.log_id === "number" ? data.log_id : null }]);
    } catch {
      setMsgs((m) => [...m, { role: "model", text: "Couldn't reach the assistant — check the connection and try again." }]);
    } finally {
      setBusy(false);
    }
  }

  // Fire the handed-off question once the popup is up (closure is fresh each
  // render, so `ask` sees current msgs/route — no stale-state double list).
  useEffect(() => {
    if (!open || !pendingQ || busy) return;
    const q = pendingQ;
    setPendingQ(null);
    void ask(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingQ, busy]);

  return (
    <>
      {/* The always-there button — out of the way until needed (and tucked
          away while the chat panel occupies the corner). */}
      {!open && !chatOpen && (
        <button
          type="button"
          data-stay-open
          onClick={() => setOpen(true)}
          title="Ask Pegasus — answers from your records"
          className={cn(
            "fixed bottom-5 right-5 z-[60] flex h-12 w-12 items-center justify-center rounded-full [pointer-events:auto]",
            "bg-gradient-to-br from-brand-600 to-brand-800 text-white shadow-lg shadow-brand-900/20",
            "transition-transform duration-200 ease-out-quart hover:scale-105 motion-reduce:hover:scale-100",
          )}
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}

      {open && (
        <div data-stay-open className="fixed bottom-5 right-5 z-[70] flex max-h-[600px] w-[380px] [pointer-events:auto] flex-col overflow-hidden rounded-2xl border border-hairline bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-brand-700 to-brand-800 px-4 py-3 text-white">
            <Sparkles className="h-4 w-4" />
            <div className="min-w-0 leading-tight">
              <h3 className="text-sm font-bold">Ask Pegasus</h3>
              <p className="text-[11px] text-white/70">Answers from your records — customers, billing history, the board, the Library, vendors, prices</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="ml-auto rounded p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3" style={{ minHeight: 180 }}>
            {msgs.length === 0 && (
              <div className="space-y-2 pt-1">
                <p className="text-[13px] text-slate-500">
                  Ask about anything in Pegasus — a customer's history, what we've billed them, today's board, who we refer, what we charge, a vendor or a teammate's number. I look it up in the records and answer right here.
                </p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => ask(s)}
                      className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div className={cn(
                  "max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed",
                  m.role === "user" ? "rounded-br-md bg-brand-700 text-white" : "rounded-bl-md bg-slate-100 text-slate-700",
                )}>
                  <AnswerText text={m.text} />
                  {m.role === "model" && m.logId != null && (
                    <div className="mt-1.5 flex items-center gap-1 border-t border-slate-200/70 pt-1 text-[11px] text-slate-400">
                      {m.verdict === "wrong" ? (
                        <span>Thanks — an admin will see this and can teach it the right answer.</span>
                      ) : m.verdict === "right" ? (
                        <span>Thanks!</span>
                      ) : (
                        <span>Right answer?</span>
                      )}
                      <span className="ml-auto flex items-center gap-0.5">
                        <button type="button" onClick={() => rate(i, "right")} title="Right"
                          className={cn("rounded p-1 hover:bg-white", m.verdict === "right" ? "text-emerald-600" : "text-slate-400 hover:text-emerald-600")}>
                          <ThumbsUp className="h-3 w-3" />
                        </button>
                        <button type="button" onClick={() => rate(i, "wrong")} title="Wrong or missing"
                          className={cn("rounded p-1 hover:bg-white", m.verdict === "wrong" ? "text-rose-600" : "text-slate-400 hover:text-rose-600")}>
                          <ThumbsDown className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2 text-[13px] text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking the records…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <form
            className="flex items-center gap-2 border-t border-slate-100 px-3 py-2.5"
            onSubmit={(e) => { e.preventDefault(); ask(input); }}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything…"
              className="flex-1 rounded-lg border border-hairline px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
            <button type="submit" disabled={busy || !input.trim()}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-700 text-white transition-colors hover:bg-brand-800 disabled:opacity-40">
              <CornerDownLeft className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
